"use client";

/**
 * MarpTemplatePicker — small dropdown for selecting a Marp CSS theme.
 *
 * Shared between `<MarpDeckEditor>` (chrome strip, top-right) and the
 * Settings surface (`MarpTemplateSettings` — same picker, different
 * context). Same brand language as `AiCellPopover` / `AiTextPopover`:
 * cream surface, hairline border, ink ramp, orange accent on focus.
 *
 * The picker is built on top of the shadcn-style `<DropdownMenu>` primitive
 * (Radix under the hood). Why Radix vs. a bare `<select>`:
 *
 *   - The cream/ink theme spills only through utility classes — a native
 *     `<select>` inherits the OS popup chrome (white card, system blue
 *     accent) and breaks the brand.
 *   - We need a divider + a "Carica tuo template..." footer item that
 *     a `<select>` can't express without `<optgroup>` hacks.
 *
 * The picker is intentionally tiny (label sits inside the trigger button,
 * panel is min-w-[200px]) so it can live in the Tab strip without competing
 * with the actual tab affordance.
 *
 * Wave 4 — May 28, 2026.
 */

import { Check, ChevronDown, Plus } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu";

/** Source of a template — drives chip styling + ordering in the panel. */
export type MarpTemplateScope = "builtin" | "custom";

/** Public shape of a single template entry. */
export interface MarpTemplateOption {
  /** Slug used as the BE identifier (e.g. ``"editorial_ando"``). */
  readonly slug: string;
  /** User-facing label (e.g. ``"Editoriale Ando"``). */
  readonly label: string;
  /** Optional one-liner description shown beneath the label. */
  readonly description?: string;
  /**
   * Source bucket. ``"custom"`` rows are listed after the divider; the
   * picker tags them with a small "Personale" chip so the user can tell
   * them apart from the four built-ins.
   */
  readonly scope?: MarpTemplateScope;
}

export interface MarpTemplatePickerProps {
  /** Currently-selected slug. */
  readonly value: string;
  /** Available templates — both builtins and customs (the picker groups them). */
  readonly templates: ReadonlyArray<MarpTemplateOption>;
  /** Fires when the user picks a different template. */
  readonly onChange: (slug: string) => void;
  /**
   * Optional — fires when the user clicks the "Carica tuo template..."
   * footer item. Typically routes to the Settings surface or opens a
   * modal. When omitted, the footer item is hidden.
   */
  readonly onUpload?: () => void;
  /**
   * When true, the picker is disabled (e.g. while the editor is mid-flight
   * on a BE call). Visual: faded chip + non-interactive.
   */
  readonly disabled?: boolean;
  readonly className?: string;
}

/** Friendly fallback for unknown slugs (custom row sans label). */
function deriveLabel(option: MarpTemplateOption): string {
  if (option.label && option.label.length > 0) return option.label;
  return option.slug;
}

export const MarpTemplatePicker: React.FC<MarpTemplatePickerProps> = ({
  value,
  templates,
  onChange,
  onUpload,
  disabled = false,
  className,
}) => {
  const builtins = React.useMemo(
    () => templates.filter((t) => (t.scope ?? "builtin") === "builtin"),
    [templates],
  );
  const customs = React.useMemo(
    () => templates.filter((t) => t.scope === "custom"),
    [templates],
  );
  const selected = React.useMemo(
    () => templates.find((t) => t.slug === value) ?? null,
    [templates, value],
  );

  const triggerLabel = selected ? deriveLabel(selected) : value;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        disabled={disabled}
        aria-label="Cambia template del deck"
        className={cn(
          // h-8 (32px) default for touch; md:h-7 collapses to 28px on
          // desktop where it sits inside the dense tab strip.
          "inline-flex h-8 max-w-[180px] items-center gap-1.5 truncate rounded-md border border-hairline bg-white/85 px-2 font-medium text-[11px] text-ink-2 transition-colors md:h-7",
          "hover:bg-white hover:text-ink-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[state=open]:border-ando-orange/40 data-[state=open]:bg-white data-[state=open]:text-ink-1",
          className,
        )}
      >
        <span className="truncate" title={triggerLabel}>
          {triggerLabel}
        </span>
        <ChevronDown
          className="h-3 w-3 shrink-0 text-ink-3"
          aria-hidden
          strokeWidth={2}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="min-w-[220px] max-w-[280px] bg-cream"
      >
        {builtins.length > 0 ? (
          <>
            <DropdownMenuLabel>Built-in</DropdownMenuLabel>
            {builtins.map((t) => (
              <PickerItem
                key={t.slug}
                option={t}
                selected={t.slug === value}
                onSelect={() => onChange(t.slug)}
              />
            ))}
          </>
        ) : null}

        {customs.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>
              I miei template ({customs.length})
            </DropdownMenuLabel>
            {customs.map((t) => (
              <PickerItem
                key={t.slug}
                option={t}
                selected={t.slug === value}
                onSelect={() => onChange(t.slug)}
              />
            ))}
          </>
        ) : null}

        {onUpload ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-ando-orange focus:text-ando-orange"
              onSelect={() => onUpload()}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden strokeWidth={2} />
              <span>Carica tuo template…</span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
MarpTemplatePicker.displayName = "MarpTemplatePicker";

interface PickerItemProps {
  readonly option: MarpTemplateOption;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

const PickerItem: React.FC<PickerItemProps> = ({
  option,
  selected,
  onSelect,
}) => (
  <DropdownMenuItem
    onSelect={() => onSelect()}
    className={cn(
      "flex flex-col items-start gap-0.5 py-1.5",
      selected && "font-semibold text-ando-orange",
    )}
  >
    <div className="flex w-full items-center gap-1.5">
      <span className="flex-1 truncate font-medium text-[12px]">
        {deriveLabel(option)}
      </span>
      {selected ? (
        <Check
          className="h-3 w-3 shrink-0 text-ando-orange"
          aria-hidden
          strokeWidth={2}
        />
      ) : null}
    </div>
    {option.description ? (
      <p className="line-clamp-2 text-[10.5px] text-ink-3">
        {option.description}
      </p>
    ) : null}
  </DropdownMenuItem>
);
PickerItem.displayName = "MarpTemplatePicker.Item";
